/**
 * Organization Context — GenesisBox
 * Holds the current user's org info after OAuth login.
 *
 * Platform and organization permissions are server-authoritative. The client
 * never promotes a user from an email allowlist or a JWT role claim.
 */

import { createContext, useCallback, useState, type ReactNode } from 'react';
import type { MemberStatus, OrgRole, StorageBackend, UserOrgSummary } from '@/types/org.types';

const ORG_KEY = 'genesisbox:org';

interface OrgState {
  id: string;
  name: string;
  role: OrgRole;
  storageBackend: StorageBackend;
  memberCount: number;
  isApproved: boolean;
  status: MemberStatus;
  isAdmin: boolean;
  adminEnabled: boolean;
  platformRole?: 'platform_owner' | null;
  canManageMembers?: boolean;
  orgs?: UserOrgSummary[];
}

interface OrgContextType {
  org: OrgState | null;
  setOrg: (org: OrgState | null) => void;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

function readStoredOrg(): OrgState | null {
  try {
    const raw = localStorage.getItem(ORG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OrgState>;
    if (!parsed.id || !parsed.name || !parsed.role || !parsed.status) return null;
    return {
      id: parsed.id,
      name: parsed.name,
      role: parsed.role,
      storageBackend: parsed.storageBackend || 'r2',
      memberCount: parsed.memberCount || 0,
      isApproved: !!parsed.isApproved,
      status: parsed.status,
      isAdmin: !!parsed.isAdmin,
      adminEnabled: !!parsed.adminEnabled,
      platformRole: parsed.platformRole === 'platform_owner' ? 'platform_owner' : null,
      canManageMembers: !!parsed.canManageMembers,
      orgs: Array.isArray(parsed.orgs) ? parsed.orgs : undefined,
    };
  } catch {
    return null;
  }
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const [org, setOrgState] = useState<OrgState | null>(() => readStoredOrg());

  const setOrg = useCallback((nextOrg: OrgState | null) => {
    setOrgState(nextOrg);
    try {
      if (nextOrg) {
        localStorage.setItem(ORG_KEY, JSON.stringify(nextOrg));
      } else {
        localStorage.removeItem(ORG_KEY);
      }
    } catch {
      // Storage persistence is a convenience; in-memory context remains usable.
    }
  }, []);

  return (
    <OrgContext.Provider value={{ org, setOrg }}>
      {children}
    </OrgContext.Provider>
  );
}

export { OrgContext };
export type { OrgState };
